// Professional Baseball Analytics Theme System
// Modern, sleek design with baseball-inspired colors and typography

import { createTheme } from '@mui/material/styles';

// Color palette inspired by professional baseball analytics platforms
const baseColors = {
  // Primary brand colors
  primary: {
    50: '#e8f4fd',
    100: '#d1e9fb',
    200: '#a3d3f7',
    300: '#75bdf3',
    400: '#47a7ef',
    500: '#1976d2', // Main primary
    600: '#1565c0',
    700: '#0d47a1',
    800: '#0a3d91',
    900: '#083371',
    main: '#1976d2', // Required by MUI
    light: '#47a7ef',
    dark: '#0d47a1',
    contrastText: '#ffffff'
  },
  
  // Secondary colors for accents
  secondary: {
    50: '#fff3e0',
    100: '#ffe7cc',
    200: '#ffcc99',
    300: '#ffb366',
    400: '#ff9933',
    500: '#ff6f00', // Main secondary
    600: '#e65100',
    700: '#cc4400',
    800: '#b33900',
    900: '#992e00',
    main: '#ff6f00', // Required by MUI
    light: '#ffcc99',
    dark: '#e65100',
    contrastText: '#ffffff'
  },

  // Success colors for positive stats
  success: {
    50: '#e8f5e8',
    100: '#c8e6c9',
    200: '#a5d6a7',
    300: '#81c784',
    400: '#66bb6a',
    500: '#4caf50',
    600: '#43a047',
    700: '#388e3c',
    800: '#2e7d32',
    900: '#1b5e20',
    main: '#4caf50', // Required by MUI
    light: '#81c784',
    dark: '#388e3c',
    contrastText: '#ffffff'
  },

  // Error colors for negative stats
  error: {
    50: '#ffebee',
    100: '#ffcdd2',
    200: '#ef9a9a',
    300: '#e57373',
    400: '#ef5350',
    500: '#f44336',
    600: '#e53935',
    700: '#d32f2f',
    800: '#c62828',
    900: '#b71c1c',
    main: '#f44336', // Required by MUI
    light: '#ef5350',
    dark: '#d32f2f',
    contrastText: '#ffffff'
  },

  // Warning colors for medium stats
  warning: {
    50: '#fff8e1',
    100: '#ffecb3',
    200: '#ffe082',
    300: '#ffd54f',
    400: '#ffca28',
    500: '#ffc107',
    600: '#ffb300',
    700: '#ffa000',
    800: '#ff8f00',
    900: '#ff6f00',
    main: '#ffc107', // Required by MUI
    light: '#ffca28',
    dark: '#ffa000',
    contrastText: '#000000'
  },

  // Neutral grays for backgrounds and text
  neutral: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121'
  },

  // Baseball field inspired colors
  field: {
    grass: '#2d5a27',
    dirt: '#8b4513',
    warning: '#ff6b35',
    foul: '#ffffff'
  }
};

// Dark theme colors — PitcherList-inspired deep navy
const darkColors = {
  background: {
    default: '#0b0f1a',
    paper: '#131829',
    elevated: '#1c2137'
  },
  surface: {
    primary: '#161b2e',
    secondary: '#1c2137',
    tertiary: '#242940'
  }
};

// Light theme colors  
const lightColors = {
  background: {
    default: '#f8fafc',
    paper: '#ffffff',
    elevated: '#ffffff'
  },
  surface: {
    primary: '#ffffff',
    secondary: '#f8fafc',
    tertiary: '#f1f5f9'
  }
};

// Typography system
const typography = {
  fontFamily: [
    'Inter',
    'SF Pro Display',
    'Segoe UI',
    '-apple-system',
    'BlinkMacSystemFont',
    'Roboto',
    'Helvetica Neue',
    'Arial',
    'sans-serif'
  ].join(','),
  
  h1: {
    fontSize: '3.5rem',
    fontWeight: 800,
    lineHeight: 1.2,
    letterSpacing: '-0.02em',
    '@media (max-width:600px)': { fontSize: '2rem' },
  },
  h2: {
    fontSize: '2.75rem',
    fontWeight: 700,
    lineHeight: 1.25,
    letterSpacing: '-0.01em',
    '@media (max-width:600px)': { fontSize: '1.75rem' },
  },
  h3: {
    fontSize: '2.25rem',
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
    '@media (max-width:600px)': { fontSize: '1.5rem' },
  },
  h4: {
    fontSize: '1.875rem',
    fontWeight: 600,
    lineHeight: 1.35,
    '@media (max-width:600px)': { fontSize: '1.35rem' },
  },
  h5: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.4,
    '@media (max-width:600px)': { fontSize: '1.15rem' },
  },
  h6: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.45,
    '@media (max-width:600px)': { fontSize: '1.05rem' },
  },
  
  // Body text
  body1: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.6
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.5
  },
  
  // Captions and small text
  caption: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.4,
    letterSpacing: '0.01em'
  },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: '0.1em',
    textTransform: 'uppercase'
  },

  // Monospace for statistics
  mono: {
    fontFamily: [
      'SF Mono',
      'Monaco',
      'Inconsolata',
      'Roboto Mono',
      'Consolas',
      'Courier New',
      'monospace'
    ].join(','),
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.4
  }
};

// Spacing system (8px base unit) — compact by default
const spacing = {
  xs: 2,
  sm: 6,
  md: 12,
  lg: 20,
  xl: 28,
  xxl: 40,
  xxxl: 56
};

// Border radius system — tighter corners
const borderRadius = {
  sm: 4,
  md: 6,
  lg: 10,
  xl: 14,
  xxl: 20
};

// Shadow system
const shadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)'
};

// Create Material-UI theme
export const createAppTheme = (mode = 'light') => {
  const isLight = mode === 'light';
  const colors = isLight ? lightColors : darkColors;

  return createTheme({
    palette: {
      mode,
      primary: baseColors.primary,
      secondary: baseColors.secondary,
      success: baseColors.success,
      error: baseColors.error,
      warning: baseColors.warning,
      background: colors.background,
      text: {
        primary: isLight ? baseColors.neutral[900] : baseColors.neutral[50],
        secondary: isLight ? baseColors.neutral[600] : baseColors.neutral[300],
        disabled: isLight ? baseColors.neutral[400] : baseColors.neutral[500]
      }
    },
    
    typography: {
      ...typography,
      allVariants: {
        fontFamily: typography.fontFamily
      }
    },

    shape: {
      borderRadius: borderRadius.md
    },

    spacing: (factor) => `${spacing.sm * factor}px`,

    components: {
      // Card styling — compact, subtle borders
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius.lg,
            boxShadow: 'none',
            border: `1px solid ${isLight ? baseColors.neutral[200] : 'rgba(255,255,255,0.06)'}`,
            transition: 'border-color 0.2s ease',
            '&:hover': {
              borderColor: isLight ? baseColors.neutral[300] : 'rgba(255,255,255,0.12)'
            }
          }
        }
      },

      // Button styling — compact, no transform
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius.md,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8rem',
            padding: `${spacing.xs}px ${spacing.md}px`,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none'
            }
          },
          contained: {
            '&:hover': {
              boxShadow: 'none'
            }
          },
          sizeSmall: {
            padding: `2px ${spacing.sm}px`,
            fontSize: '0.75rem'
          }
        }
      },

      // Paper styling — subtle
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius.lg,
            border: `1px solid ${isLight ? baseColors.neutral[200] : 'rgba(255,255,255,0.06)'}`,
            backgroundImage: 'none'
          },
          elevation1: {
            boxShadow: 'none'
          },
          elevation2: {
            boxShadow: 'none'
          },
          elevation3: {
            boxShadow: shadows.sm
          }
        }
      },

      // Table styling — ultra-compact
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: isLight ? baseColors.neutral[50] : colors.surface.secondary,
            '& .MuiTableCell-head': {
              fontWeight: 700,
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: isLight ? baseColors.neutral[600] : baseColors.neutral[400],
              borderBottom: `1px solid ${isLight ? baseColors.neutral[300] : 'rgba(255,255,255,0.08)'}`,
              whiteSpace: 'nowrap',
              padding: '6px 8px'
            }
          }
        }
      },

      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${isLight ? baseColors.neutral[200] : 'rgba(255,255,255,0.04)'}`,
            padding: '6px 8px',
            fontSize: '0.8rem'
          }
        }
      },

      // Tab styling — compact
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.8rem',
            minHeight: 44,
            padding: '4px 12px',
            '&.Mui-selected': {
              fontWeight: 700
            }
          }
        }
      },

      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 44
          }
        }
      },

      // AppBar styling — blends with dark bg
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: isLight ? colors.background.paper : colors.background.paper,
            color: isLight ? baseColors.neutral[900] : baseColors.neutral[50],
            boxShadow: 'none',
            borderBottom: `1px solid ${isLight ? baseColors.neutral[200] : 'rgba(255,255,255,0.06)'}`
          }
        }
      },

      // Chip styling — compact
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius.sm,
            fontWeight: 600,
            fontSize: '0.75rem',
            height: 24
          },
          sizeSmall: {
            height: 20,
            fontSize: '0.75rem'
          }
        }
      },

      // Tooltip — dark
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isLight ? baseColors.neutral[800] : '#0d1117',
            fontSize: '0.75rem',
            borderRadius: borderRadius.sm,
            border: `1px solid ${isLight ? 'transparent' : 'rgba(255,255,255,0.08)'}`
          }
        }
      }
    },

    // Custom theme properties
    custom: {
      colors: baseColors,
      spacing,
      borderRadius,
      shadows,
      field: baseColors.field
    }
  });
};

// Team color mappings for charts and UI
export const teamColors = {
  'NYY': '#132448',
  'BOS': '#BD3039', 
  'TOR': '#134A8E',
  'TB': '#092C5C',
  'BAL': '#DF4601',
  'HOU': '#002D62',
  'LAA': '#BA0021',
  'OAK': '#003831',
  'ATH': '#003831',
  'SEA': '#0C2C56',
  'TEX': '#003278',
  'ATL': '#CE1141',
  'WSH': '#AB0003',
  'NYM': '#002D72',
  'PHI': '#E81828',
  'MIA': '#00A3E0',
  'CHC': '#0E3386',
  'MIL': '#12284B',
  'STL': '#C41E3A',
  'CIN': '#C6011F',
  'PIT': '#FDB827',
  'LAD': '#005A9C',
  'SD': '#2F241D',
  'SF': '#FD5A1E',
  'COL': '#C4CED4',
  'ARI': '#A71930',
  'KC': '#004687',
  'MIN': '#002B5C',
  'CWS': '#27251F',
  'CLE': '#E31937',
  'DET': '#0C2340'
};

// Statistical performance color scales
export const statColors = {
  // Batting average color scale
  avg: {
    excellent: baseColors.success[600],  // .300+
    good: baseColors.success[400],       // .280-.299
    average: baseColors.warning[500],    // .250-.279
    poor: baseColors.error[400],         // .220-.249
    terrible: baseColors.error[600]      // <.220
  },

  // On-base percentage color scale
  obp: {
    excellent: baseColors.success[600],  // .380+
    good: baseColors.success[400],       // .340-.379
    average: baseColors.warning[500],    // .310-.339
    poor: baseColors.error[400],         // .280-.309
    terrible: baseColors.error[600]      // <.280
  },

  // Slugging percentage color scale
  slg: {
    excellent: baseColors.success[600],  // .500+
    good: baseColors.success[400],       // .450-.499
    average: baseColors.warning[500],    // .400-.449
    poor: baseColors.error[400],         // .350-.399
    terrible: baseColors.error[600]      // <.350
  },
  
  // ERA color scale (lower is better)
  era: {
    excellent: baseColors.success[600],  // <2.50
    good: baseColors.success[400],       // 2.50-3.49
    average: baseColors.warning[500],    // 3.50-4.49
    poor: baseColors.error[400],         // 4.50-5.49
    terrible: baseColors.error[600]      // 5.50+
  },

  // OPS color scale
  ops: {
    excellent: baseColors.success[600],  // .900+
    good: baseColors.success[400],       // .800-.899
    average: baseColors.warning[500],    // .700-.799
    poor: baseColors.error[400],         // .600-.699
    terrible: baseColors.error[600]      // <.600
  },

  // WHIP color scale (lower is better)
  whip: {
    excellent: baseColors.success[600],  // <1.00
    good: baseColors.success[400],       // 1.00-1.19
    average: baseColors.warning[500],    // 1.20-1.39
    poor: baseColors.error[400],         // 1.40-1.59
    terrible: baseColors.error[600]      // 1.60+
  },

  // WAR color scale
  war: {
    excellent: baseColors.success[600],  // 5.0+ (MVP caliber)
    good: baseColors.success[400],       // 3.0-4.9 (All-Star)
    average: baseColors.warning[500],    // 1.5-2.9 (Solid starter)
    poor: baseColors.error[400],         // 0.5-1.4 (Below average)
    terrible: baseColors.error[600]      // <0.5 (Replacement level)
  },

  // CVR v5 color scale (0-100)
  cvr: {
    excellent: baseColors.success[600],  // 80+ (Elite)
    good: baseColors.success[400],       // 65-79 (Above average)
    average: baseColors.warning[500],    // 45-64 (Average)
    poor: baseColors.error[400],         // 30-44 (Below average)
    terrible: baseColors.error[600]      // <30 (Poor)
  },

  // wOBA color scale
  woba: {
    excellent: baseColors.success[600],  // .380+ (Elite)
    good: baseColors.success[400],       // .340-.379 (Great)
    average: baseColors.warning[500],    // .315-.339 (Average)
    poor: baseColors.error[400],         // .290-.314 (Below avg)
    terrible: baseColors.error[600]      // <.290 (Poor)
  },

  // FIP color scale (lower is better)
  fip: {
    excellent: baseColors.success[600],  // <2.80 (Elite)
    good: baseColors.success[400],       // 2.80-3.49
    average: baseColors.warning[500],    // 3.50-4.19
    poor: baseColors.error[400],         // 4.20-5.00
    terrible: baseColors.error[600]      // 5.00+
  },

  // ISO (Isolated Power) color scale
  iso: {
    excellent: baseColors.success[600],  // .250+ (Elite power)
    good: baseColors.success[400],       // .200-.249 (Above avg)
    average: baseColors.warning[500],    // .140-.199 (Average)
    poor: baseColors.error[400],         // .100-.139 (Below avg)
    terrible: baseColors.error[600]      // <.100 (Weak)
  },

  // Trade Value color scale
  tradeValue: {
    excellent: '#FFD700',                // 85+ (Franchise cornerstone)
    good: '#FF6B35',                     // 70-84 (All-Star caliber)
    average: baseColors.success[500],    // 55-69 (Quality starter)
    poor: baseColors.primary[500],       // 40-54 (Solid contributor)
    terrible: baseColors.neutral[500]    // <40 (Depth piece)
  }
};

// Utility functions for theme
export const themeUtils = {
  // Get color based on statistical performance
  getStatColor: (value, statType, theme) => {
    // Safety check for theme object
    if (!theme || !theme.palette || !theme.palette.text) {
      return '#000000'; // fallback color
    }
    
    // Safety check for statType parameter
    if (!statType || typeof statType !== 'string') {
      return theme.palette.text.primary;
    }
    
    // Extract the actual stat name from nested paths like 'stats.batting.avg' -> 'avg'
    const actualStatType = statType.includes('.') ? statType.split('.').pop() : statType;
    
    const scale = statColors[actualStatType];
    if (!scale) return theme.palette.text.primary;

    switch (actualStatType) {
      case 'avg':
        if (value >= 0.300) return scale.excellent;
        if (value >= 0.280) return scale.good;
        if (value >= 0.250) return scale.average;
        if (value >= 0.220) return scale.poor;
        return scale.terrible;

      case 'obp':
        if (value >= 0.380) return scale.excellent;
        if (value >= 0.340) return scale.good;
        if (value >= 0.310) return scale.average;
        if (value >= 0.280) return scale.poor;
        return scale.terrible;

      case 'slg':
        if (value >= 0.500) return scale.excellent;
        if (value >= 0.450) return scale.good;
        if (value >= 0.400) return scale.average;
        if (value >= 0.350) return scale.poor;
        return scale.terrible;
      
      case 'era':
        if (value < 2.50) return scale.excellent;
        if (value < 3.50) return scale.good;
        if (value < 4.50) return scale.average;
        if (value < 5.50) return scale.poor;
        return scale.terrible;
      
      case 'ops':
        if (value >= 0.900) return scale.excellent;
        if (value >= 0.800) return scale.good;
        if (value >= 0.700) return scale.average;
        if (value >= 0.600) return scale.poor;
        return scale.terrible;
      
      case 'whip':
        if (value < 1.00) return scale.excellent;
        if (value < 1.20) return scale.good;
        if (value < 1.40) return scale.average;
        if (value < 1.60) return scale.poor;
        return scale.terrible;

      case 'war':
        if (value >= 5.0) return scale.excellent;
        if (value >= 3.0) return scale.good;
        if (value >= 1.5) return scale.average;
        if (value >= 0.5) return scale.poor;
        return scale.terrible;

      case 'cvr':
        if (value >= 80) return scale.excellent;
        if (value >= 65) return scale.good;
        if (value >= 45) return scale.average;
        if (value >= 30) return scale.poor;
        return scale.terrible;

      case 'woba':
        if (value >= 0.380) return scale.excellent;
        if (value >= 0.340) return scale.good;
        if (value >= 0.315) return scale.average;
        if (value >= 0.290) return scale.poor;
        return scale.terrible;

      case 'fip':
        if (value < 2.80) return scale.excellent;
        if (value < 3.50) return scale.good;
        if (value < 4.20) return scale.average;
        if (value < 5.00) return scale.poor;
        return scale.terrible;

      case 'iso':
        if (value >= 0.250) return scale.excellent;
        if (value >= 0.200) return scale.good;
        if (value >= 0.140) return scale.average;
        if (value >= 0.100) return scale.poor;
        return scale.terrible;

      case 'tradevalue':
        if (value >= 85) return scale.excellent;
        if (value >= 70) return scale.good;
        if (value >= 55) return scale.average;
        if (value >= 40) return scale.poor;
        return scale.terrible;
      
      default:
        return theme.palette.text.primary;
    }
  },

  // Get team primary color
  getTeamColor: (teamId) => {
    if (!teamId) return baseColors.neutral[500];
    const color = teamColors[teamId?.toUpperCase()];
    return color || baseColors.neutral[500];
  },

  // Get contrasting text color (supports hex #RRGGBB, rgb(), and named colors)
  getContrastText: (backgroundColor) => {
    if (!backgroundColor) return '#ffffff';
    let r, g, b;

    // Handle hex colors (#RGB or #RRGGBB)
    if (backgroundColor.startsWith('#')) {
      let hex = backgroundColor.slice(1);
      if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
      }
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    } else {
      // Handle rgb(r, g, b) or rgba(r, g, b, a)
      const rgb = backgroundColor.match(/\d+/g);
      if (!rgb || rgb.length < 3) return '#ffffff';
      r = parseInt(rgb[0]);
      g = parseInt(rgb[1]);
      b = parseInt(rgb[2]);
    }

    if (isNaN(r) || isNaN(g) || isNaN(b)) return '#ffffff';
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
  },

  // Format statistical values
  formatStat: (value, statType) => {
    if (value === null || value === undefined) return '---';
    
    const numValue = Number(value);
    if (isNaN(numValue)) return '---';

    // Percentage stats (AVG, OBP, SLG, etc.)
    if (['avg', 'obp', 'slg', 'ops', 'babip'].includes(statType.toLowerCase())) {
      return numValue.toFixed(3);
    }
    
    // ERA and WHIP
    if (['era', 'whip', 'fip'].includes(statType.toLowerCase())) {
      return numValue.toFixed(2);
    }
    
    // Innings pitched
    if (statType.toLowerCase() === 'inningspitched') {
      return numValue.toFixed(1);
    }
    
    // Whole numbers
    return Math.round(numValue).toString();
  },

  // Get position color
  getPositionColor: (position) => {
    const positionColors = {
      'P': '#e91e63', // Pitcher - Pink
      'C': '#9c27b0', // Catcher - Purple
      '1B': '#673ab7', // First Base - Deep Purple
      '2B': '#3f51b5', // Second Base - Indigo
      '3B': '#2196f3', // Third Base - Blue
      'SS': '#03a9f4', // Shortstop - Light Blue
      'LF': '#00bcd4', // Left Field - Cyan
      'CF': '#009688', // Center Field - Teal
      'RF': '#4caf50', // Right Field - Green
      'OF': '#4caf50', // Outfield - Green
      'IF': '#3f51b5', // Infield - Indigo
      'DH': '#ff9800', // Designated Hitter - Orange
      'RP': '#f44336', // Relief Pitcher - Red
      'SP': '#e91e63'  // Starting Pitcher - Pink
    };
    
    return positionColors[position] || '#757575';
  },

  // Format spacing value
  spacing: (multiplier) => `${spacing.sm * multiplier}px`,

  // Get responsive breakpoint values
  breakpoints: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920
  }
};

export default {
  createAppTheme,
  teamColors,
  statColors,
  themeUtils,
  baseColors,
  typography,
  spacing,
  borderRadius,
  shadows
};
